/**
 * CIBRAXIS · Public landing page.
 *
 * Visual identity cloned from ciberconsulting.cl:
 *   · Dark navy background (#0A0E1A → #0D1221 gradient)
 *   · Cyan primary accent (#00D4FF) with blue secondary (#3B82F6)
 *   · Inter / system-ui sans-serif
 *   · Uppercase section labels, bold section titles
 *   · Dark cards with subtle borders and cyan hover
 */
import { useState } from "react";
import {
  ShieldCheck, Radar, Zap, Globe, Boxes, Target, Fingerprint,
  TrendingUp, FileText, Clock, Lock, Users as UsersIcon, Brain,
  Mail, ArrowRight, Menu, X, Scissors, ClipboardList, CalendarRange,
  UserCheck, Bell, Search, Database, KeyRound,
} from "lucide-react";

const CYAN = "#00D4FF";
const BLUE = "#3B82F6";

const CONSOLE_URL = "https://cibraxis.com/login";

// ---------------------------------------------------------------- data

const MODULES = [
  { icon: Search, title: "Gestión de la Superficie Externa (EASM)",
    desc: "Descubrimiento continuo de subdominios, puertos, servicios y tecnologías. Diff histórico por barrido con alertas sólo si hay cambios reales. Exportación CSV y PDF ejecutivo." },
  { icon: ShieldCheck, title: "Gestión de Vulnerabilidades",
    desc: "Diez motores de escaneo orquestados más un escáner web pasivo propio. Priorización contextual severidad + KEV + EPSS + exposición pública. MTTR tracking con SLA configurable." },
  { icon: Radar, title: "Inteligencia de Amenazas",
    desc: "Dark Web Exposure, Social Media Intelligence con 11 fuentes OSINT, Data Leak Detection unificado y correlación IOC automática que bumpea la severidad de cualquier finding." },
  { icon: TrendingUp, title: "Security Ratings · A–F",
    desc: "Puntuación objetiva 0–100 fusionando 6 dimensiones ponderadas: findings, cadencia de parcheo, DNS/email, reputación IP, filtraciones y seguridad web. Historial temporal con tendencia visible." },
  { icon: Target, title: "ROI y Eficiencia Operativa",
    desc: "Hallazgos resueltos, reducción de superficie, tendencia MTTR, reducción de riesgo estimada y pérdida evitada en USD. Dashboards 30d / 90d / 1 año listos para comité." },
  { icon: ClipboardList, title: "Gestión de Remediación",
    desc: "Ticketing interno con SLA por severidad, barrido automático de vencidos y auditoría inmutable por finding. Trazabilidad completa desde detección hasta cierre." },
  { icon: Bell, title: "Motor de Alertas Inteligente",
    desc: "Reglas por tenant: findings críticos, matches KEV, expiración TLS, caídas de rating, filtraciones nuevas y cambios de superficie. Sólo notifica si hay diff real." },
  { icon: Globe, title: "Análisis DNS Avanzado",
    desc: "Resolución multi-resolver, auditoría SPF/DKIM/DMARC/CAA, detección de typosquatting y DNS drift con alertas automáticas." },
  { icon: FileText, title: "Cumplimiento Normativo",
    desc: "Mapeo automático contra el Esquema Nacional de Seguridad (ENS) en los tres niveles. Watchdog con priorización IA de gaps y exportación por framework." },
  { icon: Brain, title: "Analista IA",
    desc: "Chat contextual sobre tus datos para preguntas en lenguaje natural, generación de runbooks de mitigación y resúmenes ejecutivos listos para comité." },
];

const DISTINCTIVE = [
  { icon: Scissors, title: "Takedown de Activos Maliciosos",
    desc: "Cuando detectamos un dominio typosquatting, una página de phishing que suplanta tu marca o un repositorio con tus credenciales expuestas, nuestro equipo CTI inicia el procedimiento de takedown contra registrador, hosting y plataforma. Seguimiento 24/7 con evidencia documentada." },
  { icon: ClipboardList, title: "Cuestionarios de Seguridad a Proveedores",
    desc: "Plantillas prediseñadas ENS, ISO 27001 y SOC 2 Type II para evaluar a tus terceros. Envío automatizado, scoring de respuestas y correlación con el perfil público del proveedor para un dictamen integral." },
  { icon: CalendarRange, title: "Histórico de Exposición · 24 Meses",
    desc: "Cada activo, finding, filtración y pulse de amenazas se conserva 24 meses rodantes por cliente. Dispones de evidencia para auditorías ENS / ISO y demuestras la evolución de tu postura en el tiempo." },
  { icon: UserCheck, title: "Protección de Ejecutivos",
    desc: "Monitorizamos de forma continua a tus ejecutivos clave (CEO, CFO, CIO, CISO y uno más) en dark web, foros, pastes, infostealers y filtraciones. Reporte mensual exclusivo cifrado para el equipo directivo." },
];

const PLATFORM_SEC = [
  { icon: Lock, title: "Cifrado AES-256-SIV",
    desc: "Cifrado determinista para todos los campos sensibles en reposo. Fail-fast si la clave falta." },
  { icon: Database, title: "Aislamiento Multi-Tenant",
    desc: "Filtrado por tenant_id a nivel del wrapper de base de datos. Imposible fuga cruzada entre clientes." },
  { icon: KeyRound, title: "MFA Obligatorio",
    desc: "TOTP con códigos de recuperación. Política de contraseñas robusta con rotación e historial." },
  { icon: UsersIcon, title: "RBAC con roles",
    desc: "Scope granular por rol (admin, analista SOC, sólo lectura, cliente). Auditoría inmutable append-only de toda acción sensible." },
  { icon: Fingerprint, title: "Detección de Anomalías",
    desc: "Bursts de acceso, salto entre clientes y horarios atípicos reportados automáticamente al administrador del tenant." },
];

// ---------------------------------------------------------------- UI primitives

function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2 mb-3">
      <span className="h-px w-6" style={{ background: CYAN }} />
      <span className="text-xs tracking-[0.3em] font-semibold uppercase"
             style={{ color: CYAN }}>{children}</span>
    </div>
  );
}

function SectionTitle({ children, className = "" }) {
  return (
    <h2 className={`text-3xl md:text-4xl font-bold text-white tracking-tight ${className}`}>
      {children}
    </h2>
  );
}

function PrimaryButton({ href, children, className = "", testid, target, rel }) {
  return (
    <a href={href}
       data-testid={testid}
       target={target}
       rel={rel}
       className={`inline-flex items-center gap-2 px-6 py-3 font-semibold text-slate-950
                   transition-all duration-200 hover:brightness-110 hover:shadow-[0_0_30px_rgba(0,212,255,0.4)]
                   rounded-md ${className}`}
       style={{ background: CYAN }}>
      {children}
    </a>
  );
}

function SecondaryButton({ href, children, className = "" }) {
  return (
    <a href={href}
       className={`inline-flex items-center gap-2 px-6 py-3 font-semibold text-white
                   border-2 border-white/15 hover:border-cyan-400 transition-all duration-200
                   rounded-md ${className}`}>
      {children}
    </a>
  );
}

function FeatureCard({ icon: Icon, title, desc, accent = CYAN }) {
  return (
    <div className="group relative rounded-xl bg-slate-900/40 border border-white/5 p-6
                     hover:border-cyan-400/40 hover:bg-slate-900/70 transition-all duration-300
                     hover:translate-y-[-2px]">
      <div className="absolute -top-px left-6 right-6 h-px opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: `${accent}18`, border: `1px solid ${accent}40` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <h3 className="text-base font-semibold text-white leading-tight">{title}</h3>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
    </div>
  );
}

// ---------------------------------------------------------------- main

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white antialiased"
          style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
          data-testid="landing-root">

      {/* ========================= NAVBAR ========================= */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-[#0A0E1A]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <a href="#inicio" className="flex items-center gap-3" data-testid="landing-logo">
            <img src="/assets/cibraxis-logo.png" alt="CIBRAXIS"
                  className="w-11 h-11 object-contain" />
            <div className="leading-none">
              <div className="text-xl font-bold tracking-tight">CIBRAXIS</div>
              <div className="font-mono text-[9px] text-slate-400 tracking-[0.2em] uppercase mt-1">
                By Ciberconsulting
              </div>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-8 text-sm">
            <a href="#capacidades" className="text-slate-300 hover:text-white transition-colors">Capacidades</a>
            <a href="#distintivo" className="text-slate-300 hover:text-white transition-colors">Servicios</a>
            <a href="#seguridad" className="text-slate-300 hover:text-white transition-colors">Seguridad</a>
            <a href="#contacto" className="text-slate-300 hover:text-white transition-colors">Contacto</a>
          </div>

          <div className="flex items-center gap-3">
            <a href={CONSOLE_URL}
                data-testid="landing-nav-console-btn"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-950
                           rounded-md transition-all hover:brightness-110
                           hover:shadow-[0_0_20px_rgba(0,212,255,0.5)]"
                style={{ background: CYAN }}>
              <KeyRound className="w-4 h-4" />
              Acceso Consola
            </a>
            <button onClick={() => setMenuOpen(!menuOpen)}
                    className="md:hidden p-2 text-slate-300 hover:text-white"
                    data-testid="landing-mobile-menu-btn"
                    aria-label="Toggle menu">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-white/5 bg-[#0A0E1A]/95">
            <div className="px-4 py-4 flex flex-col gap-3 text-sm">
              <a href="#capacidades" onClick={() => setMenuOpen(false)} className="text-slate-300 hover:text-white">Capacidades</a>
              <a href="#distintivo" onClick={() => setMenuOpen(false)} className="text-slate-300 hover:text-white">Servicios</a>
              <a href="#seguridad" onClick={() => setMenuOpen(false)} className="text-slate-300 hover:text-white">Seguridad</a>
              <a href="#contacto" onClick={() => setMenuOpen(false)} className="text-slate-300 hover:text-white">Contacto</a>
            </div>
          </div>
        )}
      </nav>

      {/* ========================= HERO ========================= */}
      <section id="inicio" className="relative overflow-hidden">
        {/* ambient grid + glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage:
            "linear-gradient(rgba(0,212,255,0.05) 1px, transparent 1px), " +
            "linear-gradient(90deg, rgba(0,212,255,0.05) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at top, black, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at top, black, transparent 70%)",
        }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${CYAN}22, transparent 60%)` }} />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="max-w-3xl mx-auto text-center" data-testid="landing-hero-content">
            <SectionLabel>Ciberseguridad continua</SectionLabel>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight mb-6">
              La amenaza no espera.
              <br />
              <span style={{ color: CYAN }}>Tu visibilidad tampoco debería.</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-4 max-w-2xl mx-auto">
              Cada día nacen nuevos dominios maliciosos, filtran credenciales de
              tus empleados en la dark web y aparecen vulnerabilidades críticas.
              El equipo humano no puede estar en todo. Tu CISO necesita
              evidencia, no sospechas.
            </p>
            <p className="text-lg text-white font-semibold leading-relaxed mb-8 max-w-2xl mx-auto">
              CIBRAXIS es la plataforma enterprise de monitoreo cibernético
              continuo que descubre, prioriza, remedia y demuestra el retorno —
              con cobertura 24/7, en español, multi-tenant.
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              <SecondaryButton href="#capacidades">
                Ver capacidades
                <ArrowRight className="w-4 h-4" />
              </SecondaryButton>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-3xl mx-auto">
              {[
                ["30+", "Fuentes de inteligencia"],
                ["13", "Módulos operativos"],
                ["24 meses", "Histórico por cliente"],
                ["A–F", "Rating objetivo"],
              ].map(([v, l]) => (
                <div key={l} className="rounded-lg bg-slate-900/40 border border-white/5 p-4">
                  <div className="text-2xl md:text-3xl font-bold" style={{ color: CYAN }}>{v}</div>
                  <div className="text-xs text-slate-400 mt-1 tracking-wide">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================= CAPACIDADES ========================= */}
      <section id="capacidades" className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <SectionLabel>Plataforma</SectionLabel>
            <SectionTitle>Capacidades de la plataforma</SectionTitle>
            <p className="text-slate-400 text-base mt-3 max-w-2xl mx-auto">
              Diez módulos nativos trabajando en conjunto para darte visibilidad total,
              priorización contextual y demostración de retorno en un solo panel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULES.map(m => (
              <FeatureCard key={m.title} {...m} />
            ))}
          </div>
        </div>
      </section>

      {/* ========================= SERVICIOS DISTINTIVOS ========================= */}
      <section id="distintivo"
                className="relative py-20 md:py-28 border-t border-white/5"
                style={{ background: "radial-gradient(ellipse at center, rgba(59,130,246,0.08), transparent 70%)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <SectionLabel>Diferencial CIBRAXIS</SectionLabel>
            <SectionTitle>Servicios distintivos</SectionTitle>
            <p className="text-slate-400 text-base mt-3 max-w-2xl mx-auto">
              Lo que te quita el trabajo manual que ningún otro vendor cubre a la vez.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {DISTINCTIVE.map(d => (
              <FeatureCard key={d.title} {...d} accent={BLUE} />
            ))}
          </div>
        </div>
      </section>

      {/* ========================= SEGURIDAD DE LA PLATAFORMA ========================= */}
      <section id="seguridad" className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <SectionLabel>Confianza enterprise</SectionLabel>
            <SectionTitle>La seguridad de la plataforma, al nivel que exiges</SectionTitle>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PLATFORM_SEC.map(s => (
              <FeatureCard key={s.title} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* ========================= CTA ========================= */}
      <section id="contacto"
                className="relative py-20 md:py-28 border-t border-white/5"
                style={{ background: `linear-gradient(180deg, transparent, ${CYAN}08)` }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <SectionLabel>Siguiente paso</SectionLabel>
          <SectionTitle className="mb-5">¿Quieres ver cómo se ve tu superficie de ataque hoy?</SectionTitle>
          <p className="text-slate-300 text-base mb-10 max-w-2xl mx-auto">
            Agenda una demo de 30 minutos y te mostramos en vivo el estado actual
            de un dominio tuyo — sin compromiso.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 max-w-3xl mx-auto">
            <div className="rounded-xl bg-slate-900/40 border border-white/5 p-5 text-left">
              <Zap className="w-5 h-5 mb-2" style={{ color: CYAN }} />
              <div className="font-semibold mb-1">Demo guiada</div>
              <div className="text-sm text-slate-400">30 minutos sobre tu dominio</div>
            </div>
            <div className="rounded-xl bg-slate-900/40 border border-white/5 p-5 text-left">
              <Target className="w-5 h-5 mb-2" style={{ color: CYAN }} />
              <div className="font-semibold mb-1">Diagnóstico inicial</div>
              <div className="text-sm text-slate-400">Gratuito de superficie externa</div>
            </div>
            <div className="rounded-xl bg-slate-900/40 border border-white/5 p-5 text-left">
              <Clock className="w-5 h-5 mb-2" style={{ color: CYAN }} />
              <div className="font-semibold mb-1">Piloto 30 días</div>
              <div className="text-sm text-slate-400">Hasta 3 clientes</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <PrimaryButton href="mailto:contacto@ciberconsulting.cl"
                            testid="landing-cta-email-btn">
              <Mail className="w-4 h-4" />
              contacto@ciberconsulting.cl
            </PrimaryButton>
          </div>
        </div>
      </section>

      {/* ========================= FOOTER ========================= */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" style={{ color: CYAN }} />
            <span>CIBRAXIS · Ciberseguridad continua, sin puntos ciegos.</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={CONSOLE_URL} target="_blank" rel="noopener noreferrer"
                className="hover:text-white transition-colors">Acceso Consola</a>
            <a href="mailto:contacto@ciberconsulting.cl" className="hover:text-white transition-colors">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
